import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Globe, Users, BookOpen, Leaf, Award, Target } from 'lucide-react';

const Overview = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight mb-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
              Centre for
              <span className="block font-normal mt-2">Sustainable AI</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed font-medium">
              Global Leadership in Ethical, Responsible, and Sustainable Artificial Intelligence
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The Centre for Sustainable AI is a global, independent organisation dedicated to advancing ethical, responsible, and sustainable artificial intelligence. We operate at the intersection of research, governance, policy, and leadership, supporting governments, industry, and civil society to navigate the rapidly evolving risks and opportunities of AI.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              As AI systems increasingly shape economies, public services, security, and social outcomes, the need for strong governance, accountability, and sustainability has never been greater. The Centre for Sustainable AI provides thought leadership and practical expertise to ensure AI technologies are developed and deployed in ways that are trustworthy, human-centred, and aligned with long-term societal and environmental goals.
            </p>
          </div>
        </div>
      </section>

      {/* Global Role Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                  <Globe className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
                  Our Global Role and Engagement
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The Centre for Sustainable AI works closely with global peak bodies and international standards organisations, including the OECD, the IEEE Standards Association, ISO, and the IET. Through these engagements, we contribute to their initiatives and collaborate on global movements in line with UN SDG 17—shaping international norms, standards, and best practices that influence AI governance worldwide.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our work spans global, regional, and national contexts, helping translate high-level principles into implementable policies, regulatory frameworks, and organisational governance models. We focus on real-world impact—bridging the gap between emerging AI risks, ethical principles, and operational decision-making.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {['OECD', 'IEEE', 'ISO', 'IET'].map((org, index) => (
                <Card key={org} className="hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <p className="text-2xl font-light">{org}</p>
                    <p className="text-sm text-gray-500 mt-2">Collaborative Engagement</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Sector Collaboration */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
              <Users className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
              Cross-Sector Collaboration and Global Impact
            </h2>
          </div>
          
          <div className="max-w-4xl">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Beyond standards and policy leadership, the Centre for Sustainable AI collaborates across business, environmental, humanitarian, and ethics ecosystems to ensure AI governance reflects real-world complexity and societal impact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-normal mb-4">Business Leadership</h3>
                <p className="text-gray-600 leading-relaxed">
                  We engage with organisations such as Harvard Business Review to inform executive-level thinking on responsible AI leadership and governance.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-normal mb-4">Environmental Partnership</h3>
                <p className="text-gray-600 leading-relaxed">
                  Working with environmental organisations including World Wide Fund for Nature to align AI innovation with sustainability and climate priorities.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-normal mb-4">Humanitarian Focus</h3>
                <p className="text-gray-600 leading-relaxed">
                  We collaborate with organisations such as the International Federation of Red Cross and Red Crescent Societies to support ethical AI use in crisis response and humanitarian decision-making.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-normal mb-4">Ethics & Research</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our ethics work is strengthened through engagement with global AI ethics institutes and research networks, ensuring alignment with emerging norms and human-centred principles.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-normal mb-4">Global Development</h3>
                <p className="text-gray-600 leading-relaxed">
                  We work with the United Nations Development Programme and national governments to support the design of inclusive AI policies and governance frameworks.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-normal mb-4">Women in AI</h3>
                <p className="text-gray-600 leading-relaxed">
                  We are proud contributors to Women in AI initiatives, supporting women in STEM and advancing inclusive, ethical approaches to human-centred intelligence.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Research & Policy */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
              Research, Policy, and Governance Leadership
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Consultants and researchers at the Centre for Sustainable AI actively collaborate with universities, research institutes, and interdisciplinary research programmes across multiple jurisdictions.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                In parallel, we work directly with national governments and public sector bodies to support the development of AI ethics policies, national AI strategies, standards frameworks, and assurance mechanisms. This includes advising on regulatory readiness, institutional capability building, and governance structures that can evolve alongside rapid technological change.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-normal mb-6">Our Research Agenda</h3>
              <ul className="space-y-4">
                {[
                  'AI risk identification and mitigation',
                  'Accountability, transparency, and explainability',
                  'Responsible deployment of generative and autonomous systems',
                  'AI governance for safety-critical and high-impact domains',
                  'Alignment of AI systems with human values and democratic institutions'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability & UN SDGs */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
              <Leaf className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
              Sustainability and the UN Sustainable Development Goals
            </h2>
          </div>

          <div className="max-w-4xl">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Sustainability is a core pillar of our work. The Centre for Sustainable AI aligns its activities with the United Nations Sustainable Development Goals, recognising that AI plays a critical role in shaping economic resilience, environmental stewardship, and social equity.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We support organisations in leveraging AI responsibly to advance sustainability outcomes—while ensuring that AI systems themselves do not introduce new social, environmental, or ethical harms. Our work helps organisations embed sustainable AI practices within their corporate social responsibility (CSR) strategies, governance structures, and long-term value creation models.
            </p>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
              <Award className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
              What Sets the Centre for Sustainable AI Apart
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Global Credibility',
                description: 'Active engagement with international standards and policy bodies'
              },
              {
                title: 'Research-Driven Insights',
                description: 'Grounded in interdisciplinary scholarship and applied practice'
              },
              {
                title: 'Policy Expertise',
                description: 'Informed by direct government collaboration'
              },
              {
                title: 'Practical Frameworks',
                description: 'Governance frameworks that organisations can operationalise'
              },
              {
                title: 'Sustainability-First',
                description: 'Perspective aligned with global development priorities'
              },
              {
                title: 'Real-World Focus',
                description: 'Essential foundations for resilient institutions and trusted technologies'
              }
            ].map((item, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <h3 className="text-xl font-normal mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 max-w-4xl">
            <p className="text-lg text-gray-700 leading-relaxed italic">
              We do not view AI ethics and governance as abstract ideals, but as essential foundations for resilient institutions, trusted technologies, and sustainable societies.
            </p>
          </div>
        </div>
      </section>

      {/* Our Purpose */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                <Target className="w-7 h-7 text-black" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-8">
              Our Purpose
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              The Centre for Sustainable AI exists to strengthen global AI ethics and governance by supporting leaders, policymakers, and organisations to anticipate and mitigate emerging AI risks—while enabling innovation that serves humanity and the planet.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              Through leadership, collaboration, and evidence-based policy engagement, we help shape a future where artificial intelligence is safe, accountable, and sustainable by design.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Overview;
